import React, { useState, useEffect } from "react";
import { Plus, FileText, MessageSquare, FolderOpen } from "lucide-react";
import type { CreateTaskInput, TaskItemType, TaskFrequency } from "../../../types/task";
import { ApiService } from "../../../services/api";
import Modal from "../../../components/ui/Modal";
import DepartmentSelector from "../../../components/selectors/DepartmentSelector";
import UserSelector from "../../../components/selectors/UserSelector";

export interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (task: CreateTaskInput) => void;
  initialData?: CreateTaskInput;
}

interface DepartmentDetail {
  id: string;
  name: string;
  color?: string;
}

interface UserDetail {
  id: string;
  firstName: string;
  lastName: string;
}

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState<CreateTaskInput>({
    title: "",
    description: "",
    itemType: "TEXT",
    frequency: "ONCE",
    startDate: "",
    endDate: "",
    departmentIds: [],
    userIds: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentSelectedDepartment, setCurrentSelectedDepartment] = useState<string>("");
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, DepartmentDetail>>({});
  const [selectedUserDetails, setSelectedUserDetails] = useState<Record<string, UserDetail>>({});
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          title: initialData.title || "",
          description: initialData.description || "",
          itemType: initialData.itemType || "TEXT",
          frequency: initialData.frequency || "ONCE",
          startDate: initialData.startDate || "",
          endDate: initialData.endDate || "",
          departmentIds: initialData.departmentIds || [],
          userIds: initialData.userIds || [],
        });

        if (initialData.departmentIds && initialData.departmentIds.length > 0) {
          setLoadingDepartments(true);
          const promises = initialData.departmentIds.map(id => ApiService.getDepartment(id));
          Promise.all(promises)
            .then((responses) => {
              const detailsMap: Record<string, DepartmentDetail> = {};
              responses.forEach((res: any) => {
                const dept = res.data || res;
                if (dept && dept.id) {
                  detailsMap[dept.id] = {
                    id: dept.id,
                    name: dept.name,
                    color: dept.color,
                  };
                }
              });
              setSelectedDepartmentDetails(detailsMap);
            })
            .catch(console.error)
            .finally(() => setLoadingDepartments(false));
        } else {
          setSelectedDepartmentDetails({});
        }

        if (initialData.userIds && initialData.userIds.length > 0) {
          setLoadingUsers(true);

          Promise.all(
            initialData.userIds.map((userId: string) =>
              ApiService.getUser(userId).catch(() => null)
            )
          )
            .then((users) => {
              const userMap: Record<string, UserDetail> = {};
              users.forEach((user: any) => {
                if (user) {
                  userMap[user.id] = {
                    id: user.id,
                    firstName: user.firstName || "",
                    lastName: user.lastName || "",
                  };
                }
              });
              setSelectedUserDetails(userMap);
            })
            .catch(console.error)
            .finally(() => setLoadingUsers(false));
        } else {
          setSelectedUserDetails({});
        }
      } else {
        setFormData({
          title: "",
          description: "",
          itemType: "TEXT",
          frequency: "ONCE",
          startDate: "",
          endDate: "",
          departmentIds: [],
          userIds: [],
        });
        setSelectedDepartmentDetails({});
        setSelectedUserDetails({});
      }
      setErrors({});
      setCurrentSelectedDepartment("");
    }
  }, [isOpen, initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev: CreateTaskInput) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [name]: "" }));
    }
  };

  const handleItemTypeChange = (type: TaskItemType) => {
    setFormData((prev: CreateTaskInput) => ({ ...prev, itemType: type }));
  };

  const handleFrequencyChange = (frequency: TaskFrequency) => {
    setFormData((prev: CreateTaskInput) => ({ ...prev, frequency }));
  };

  const handleDepartmentAdd = (departmentId: string, department?: any) => {
    if (departmentId && !(formData.departmentIds || []).includes(departmentId)) {
      setFormData((prev) => ({
        ...prev,
        departmentIds: [...(prev.departmentIds || []), departmentId],
      }));

      if (department) {
        setSelectedDepartmentDetails((prev) => ({
          ...prev,
          [departmentId]: {
            id: department.id,
            name: department.name,
            color: department.color,
          },
        }));
      }

      setCurrentSelectedDepartment("");

      if (errors.departmentIds) {
        setErrors((prev) => ({ ...prev, departmentIds: "" }));
      }
    }
  };

  const handleDepartmentRemove = (departmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      departmentIds: (prev.departmentIds || []).filter((id) => id !== departmentId),
    }));

    setSelectedDepartmentDetails((prev) => {
      const updated = { ...prev };
      delete updated[departmentId];
      return updated;
    });
  };

  const handleUserAdd = (userId: string, user?: any) => {
    if (userId && !(formData.userIds || []).includes(userId)) {
      setFormData((prev) => ({
        ...prev,
        userIds: [...(prev.userIds || []), userId],
      }));

      if (user) {
        setSelectedUserDetails((prev) => ({
          ...prev,
          [userId]: {
            id: user.id,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
          },
        }));
      }

      if (errors.departmentIds) {
        setErrors((prev) => ({ ...prev, departmentIds: "" }));
      }
    }
  };

  const handleUserRemove = (userId: string) => {
    setFormData((prev) => ({
      ...prev,
      userIds: (prev.userIds || []).filter((id) => id !== userId),
    }));

    setSelectedUserDetails((prev) => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Görev adı gereklidir";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Görev açıklaması gereklidir";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Başlangıç tarihi gereklidir";
    }

    if (!formData.endDate) {
      newErrors.endDate = "Bitiş tarihi gereklidir";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = "Bitiş tarihi başlangıç tarihinden sonra olmalıdır";
      }
    }

    const hasDeps = formData.departmentIds && formData.departmentIds.length > 0;
    const hasUsers = formData.userIds && formData.userIds.length > 0;

    if (!hasDeps && !hasUsers) {
      newErrors.departmentIds = "En az bir departman veya kullanıcı seçmelisiniz.";
    } else if (hasDeps && hasUsers) {
      newErrors.departmentIds = "Lütfen SADECE departman veya SADECE kullanıcı seçiniz, ikisini aynı anda seçemezsiniz.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Yeni Görev Oluştur" size="lg">
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-4 pb-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Görev Adı <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.title ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="Örn: Haftalık Ödev Gönderimi"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Görev Açıklaması <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${errors.description ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="Görev hakkında detaylı açıklama yazın..."
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Görev Sıklığı <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "ONCE", label: "Bir Kez" },
                { value: "DAILY", label: "Günlük" },
                { value: "WEEKLY", label: "Haftalık" },
                { value: "MONTHLY", label: "Aylık" },
              ].map((freq) => (
                <button
                  key={freq.value}
                  type="button"
                  onClick={() => handleFrequencyChange(freq.value as TaskFrequency)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${formData.frequency === freq.value ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}>
                  {freq.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              İstenilen Öge <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => handleItemTypeChange("TEXT")}
                className={`px-4 py-2.5 border-2 rounded-lg transition-all ${formData.itemType === "TEXT" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-gray-300"
                  }`}>
                <div className="flex items-center justify-center gap-2">
                  <MessageSquare className={`w-5 h-5 ${formData.itemType === "TEXT" ? "text-purple-600" : "text-gray-400"}`} />
                  <span className={`text-sm font-medium ${formData.itemType === "TEXT" ? "text-purple-900" : "text-gray-700"}`}>Yazı</span>
                </div>
              </button>

              <button
                type="button"
                disabled
                className="px-4 py-2.5 border-2 rounded-lg border-gray-200 bg-gray-100 cursor-not-allowed opacity-60">
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Dosya</span>
                </div>
              </button>

              <button
                type="button"
                disabled
                className="px-4 py-2.5 border-2 rounded-lg border-gray-200 bg-gray-100 cursor-not-allowed opacity-60">
                <div className="flex items-center justify-center gap-2">
                  <FolderOpen className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Her İkisi</span>
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Başlangıç Tarihi <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.startDate ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                Bitiş Tarihi <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.endDate ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departmanlar (İsteğe Bağlı)
            </label>

            {(formData.departmentIds || []).length > 0 && (
              <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-sm font-medium text-purple-900 mb-3">Seçili Departmanlar ({(formData.departmentIds || []).length})</div>
                <div className="flex flex-wrap gap-2">
                  {(formData.departmentIds || []).map((deptId) => {
                    const dept = selectedDepartmentDetails[deptId];
                    return dept ? (
                      <span
                        key={deptId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {dept.color && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />}
                        <span className="truncate">{dept.name}</span>
                        <button
                          type="button"
                          onClick={() => handleDepartmentRemove(deptId)}
                          className="text-purple-600 hover:text-purple-800 font-bold transition-colors ml-1">
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            <DepartmentSelector
              value={currentSelectedDepartment}
              onChange={handleDepartmentAdd}
              placeholder="Departman ara..."
              excludeIds={formData.departmentIds || []}
              allowClear={false}
              required={false}
              showColors={true}
              inline={true}
              className="text-sm"
            />

            {errors.departmentIds && <p className="mt-2 text-sm text-red-600">{errors.departmentIds}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Kullanıcılar (İsteğe Bağlı)
            </label>

            {formData.userIds && formData.userIds.length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm font-medium text-blue-900 mb-3">Seçili Kullanıcılar ({formData.userIds.length})</div>
                <div className="flex flex-wrap gap-2">
                  {formData.userIds.map((userId) => {
                    const user = selectedUserDetails[userId];
                    return (
                      <span
                        key={userId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                          {user ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : "?"}
                        </div>
                        <span className="truncate">
                          {user ? `${user.firstName} ${user.lastName}` : userId}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUserRemove(userId)}
                          className="text-blue-600 hover:text-blue-800 font-bold transition-colors ml-1">
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <UserSelector
              value={null}
              onChange={handleUserAdd}
              placeholder="Kullanıcı ara..."
              excludeIds={formData.userIds || []}
              allowClear={false}
              required={false}
              inline={true}
              hideSelected={true}
              className="text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all">
            İptal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 hover:shadow-lg transition-all inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Görev Oluştur
          </button>
        </div>
      </form>
    </Modal>
  );
};